import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Icona } from '@/components/Icona';
import { nodesPlans, useEstructura, type NodePla, type TipusNode } from './estructura';

const normalitza = (s: string) =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

/**
 * Cercador de departament / àrea / servei: s'escriu una part del nom i es tria de la llista,
 * que mostra de quin tipus és cada resultat i on és dins la jerarquia.
 */
export function CercaAmbit({
  onTria,
  exclou,
  tipus = ['DEPARTAMENT', 'AREA', 'SERVEI'],
  placeholder,
  'aria-label': ariaLabel,
  autoFocus,
}: {
  onTria: (n: NodePla) => void;
  /** Nodes que no s'han d'oferir (p. ex. ja assignats). */
  exclou?: (n: NodePla) => boolean;
  tipus?: TipusNode[];
  placeholder?: string;
  'aria-label'?: string;
  autoFocus?: boolean;
}) {
  const { t } = useTranslation('administracio');
  const e = useEstructura();
  const id = useId();
  const [q, setQ] = useState('');
  const [actiu, setActiu] = useState(0);
  const [obert, setObert] = useState(false);
  const camp = useRef<HTMLInputElement>(null);
  const tots = useMemo(() => nodesPlans(e), [e]);
  const resultats = useMemo(() => {
    const n = normalitza(q.trim());
    if (!n) return [];
    return tots
      .filter((x) => tipus.includes(x.tipus) && x.actiu && !exclou?.(x))
      .filter((x) => normalitza(`${x.nom} ${x.codi ?? ''}`).includes(n))
      .slice(0, 8);
  }, [tots, q, tipus, exclou]);

  const tria = (n: NodePla) => {
    onTria(n);
    setQ('');
    setObert(false);
    camp.current?.focus();
  };
  const tecla = (ev: KeyboardEvent) => {
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      setActiu((i) => Math.min(i + 1, resultats.length - 1));
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      setActiu((i) => Math.max(i - 1, 0));
    } else if (ev.key === 'Enter') {
      const r = resultats[actiu];
      if (r) {
        ev.preventDefault();
        tria(r);
      }
    } else if (ev.key === 'Escape') {
      setObert(false);
    }
  };
  const etiquetaTipus = (tp: TipusNode) =>
    t(
      `marc.estructura.${tp === 'DEPARTAMENT' ? 'departament' : tp === 'AREA' ? 'area' : 'servei'}`,
    );
  const mostra = obert && q.trim() !== '';

  return (
    <div className="cerca-ambit">
      <label className="search cerca-ambit__camp">
        <Icona nom="plus" />
        <input
          ref={camp}
          value={q}
          role="combobox"
          aria-expanded={mostra}
          aria-controls={`${id}-l`}
          aria-autocomplete="list"
          aria-activedescendant={mostra && resultats[actiu] ? `${id}-${actiu}` : undefined}
          aria-label={ariaLabel ?? placeholder ?? t('marc.estructura.cerca')}
          placeholder={placeholder ?? t('marc.estructura.cerca')}
          autoFocus={autoFocus}
          onChange={(ev) => {
            setQ(ev.target.value);
            setActiu(0);
            setObert(true);
          }}
          onFocus={() => setObert(true)}
          onBlur={() => window.setTimeout(() => setObert(false), 150)}
          onKeyDown={tecla}
        />
      </label>
      {mostra ? (
        <div className="cerca-ambit__llista" id={`${id}-l`} role="listbox">
          {resultats.length === 0 ? (
            <div className="desplegable__buit">{t('marc.estructura.senseResultats', { q })}</div>
          ) : (
            resultats.map((r, i) => (
              <div
                key={`${r.tipus}-${r.id}`}
                id={`${id}-${i}`}
                role="option"
                aria-selected={i === actiu}
                className={`desplegable__opcio${i === actiu ? ' is-focus' : ''}`}
                onMouseEnter={() => setActiu(i)}
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={() => tria(r)}
              >
                <span
                  className={`node-punt node-punt--${r.tipus.toLowerCase()}`}
                  aria-hidden="true"
                />
                <span className="desplegable__text">
                  <span className="desplegable__etiqueta">{r.nom}</span>
                  <span className="desplegable__detall">
                    {etiquetaTipus(r.tipus)}
                    {r.ruta ? ` · ${r.ruta}` : ''}
                  </span>
                </span>
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
